import { getAbsenceCategoryLabel, ABSENCE_CATEGORY_COLOR } from '../../../entities/absence'
import { getAppointmentAttachmentTotal } from '../../../entities/appointment'
import { workOrderAddress } from '../../../shared/helpers/address'
import { resolveBlockTimes } from '../../../shared/lib/schedule'
import type { TransferSets, TravelKind } from '../../../shared/lib/schedule'
import {
  COMPLETED_TRAVEL_COLOR,
  getAppointmentColors,
  getTaskColors,
} from '../../../shared/lib/timelineColors'
import type { CalendarBlock } from './buildDayBlocks'

export type { TravelKind }

/**
 * Everything a worker-calendar block *says*, resolved before anything is painted.
 *
 * The worker counterpart of the board's `buildSegments`: the board pre-resolves `segments[]` in
 * its model and renders every kind through one `ScheduleBlock`; this does the same for the
 * vertical calendar. The four block components it replaced each carried their own copy of the
 * colour, label and running-state rules, and two of them were the same file with different data.
 *
 * Pure — no React, no geometry. Pixel heights and the density ladder stay in the component,
 * because they depend on the grid, not on the entry.
 */

export interface JobBlockView {
  layout: 'job'
  /** The aria prefix, and the noun the block is to a screen reader. */
  kindLabel: 'Termin' | 'Aufgabe'
  workColor: string
  travelColor: string
  departureColor: string
  travelKind: TravelKind
  departureKind: TravelKind
  /** `actual ?? planned` per component — how the box is divided, never where it sits. */
  minutes: [travel: number, work: number, departure: number]
  timeLabel: string
  /** The location for a Termin, the title for an Aufgabe. */
  subject: string | null
  who: string | null
  contact: string | null
  attachmentCount: number
  /** Work itself is running — the `Läuft` marker. */
  running: boolean
  /** Any phase is open — the pulsing ring, worn at every density. */
  ringed: boolean
  /** Everything the block may be too short to show; the tooltip and the rest of the aria-label. */
  description: string
}

export interface BarBlockView {
  layout: 'bar'
  color: string
  icon: 'coffee' | 'user-minus'
  label: string
  /** A second fact the bar can show when it has the room — the Pause's length. */
  meta: string | null
  description: string
}

export type BlockView = JobBlockView | BarBlockView

export interface BlockViewContext {
  /** From `detectTransfers`, keyed by `block.key` — which Anfahrt/Abfahrt strips are a handover. */
  transfers: TransferSets
  /** The same clock `buildDayBlocks` drew the span with, so the strips divide the stretched box. */
  nowMs?: number
}

/**
 * `null` only for a block whose owner is missing — a kind/owner mismatch `buildDayBlocks` never
 * produces, kept so the caller narrows on the view rather than on four optional fields.
 */
export function resolveBlockView(block: CalendarBlock, ctx: BlockViewContext): BlockView | null {
  switch (block.kind) {
    case 'APPOINTMENT': {
      const appointment = block.appointment
      if (!appointment) return null

      const times = resolveBlockTimes(appointment.schedule, ctx.nowMs)
      const { workColor, travelColor } = getAppointmentColors(appointment.status)
      const ext = appointment.workOrder?.external ?? null
      const subject = workOrderAddress(ext)
      const who = ext?.mieter?.name ?? null
      const contact = ext?.mieter?.telefon ?? null

      return {
        layout: 'job',
        kindLabel: 'Termin',
        workColor,
        // The drive out is over once the work runs — drawn as done.
        travelColor: times.isInProgress ? COMPLETED_TRAVEL_COLOR : travelColor,
        departureColor: travelColor,
        travelKind: ctx.transfers.travel.has(block.key) ? 'Transfer' : 'Anfahrt',
        departureKind: ctx.transfers.departure.has(block.key) ? 'Transfer' : 'Abfahrt',
        minutes: [times.travelMinutes, times.workMinutes, times.departureMinutes],
        timeLabel: times.timeLabel,
        subject,
        who,
        contact,
        attachmentCount: getAppointmentAttachmentTotal(appointment),
        running: times.isInProgress,
        ringed: times.isSessionOpen,
        description: joinDescription([times.timeLabel, times.phaseLabel, subject, who, contact]),
      }
    }

    case 'TASK': {
      const task = block.task
      if (!task?.schedule) return null

      const times = resolveBlockTimes(task.schedule, ctx.nowMs)
      const { workColor, travelColor } = getTaskColors(task.status)

      return {
        layout: 'job',
        kindLabel: 'Aufgabe',
        workColor,
        travelColor: times.isInProgress ? COMPLETED_TRAVEL_COLOR : travelColor,
        departureColor: travelColor,
        travelKind: ctx.transfers.travel.has(block.key) ? 'Transfer' : 'Anfahrt',
        departureKind: ctx.transfers.departure.has(block.key) ? 'Transfer' : 'Abfahrt',
        minutes: [times.travelMinutes, times.workMinutes, times.departureMinutes],
        timeLabel: times.timeLabel,
        subject: task.title,
        who: null,
        contact: null,
        attachmentCount: 0,
        running: times.isInProgress,
        ringed: times.isSessionOpen,
        description: joinDescription([times.timeLabel, times.phaseLabel, task.title]),
      }
    }

    case 'ABSENCE': {
      const entry = block.entry
      if (!entry) return null
      const label = getAbsenceCategoryLabel(entry.category)

      return {
        layout: 'bar',
        color: ABSENCE_CATEGORY_COLOR,
        icon: 'user-minus',
        label,
        meta: null,
        description: label,
      }
    }

    case 'BREAK': {
      // From the server's own BREAK entry via `buildDayBlocks`, so it reflects
      // `TechnicianProfile.breakDurationMinutes` rather than an assumed 30.
      const meta = block.durationMinutes != null ? `${block.durationMinutes} Min` : null

      return {
        layout: 'bar',
        color: BREAK_COLOR,
        icon: 'coffee',
        label: 'Pause',
        meta,
        description: joinDescription(['Pause', meta]),
      }
    }

    default:
      return null
  }
}

/** Matches the board's `BREAK_COLOR`. */
export const BREAK_COLOR = '#94A3B8'

const joinDescription = (parts: Array<string | null | undefined>): string =>
  parts.filter(Boolean).join(' · ')
